import { createContext, useContext } from 'react';
import type { LearnerPreferences, LearnerProfile } from '@/domain/records';
import type { DeviceSettings } from '@/services/db/meta';

export interface AppState {
  /** False until the first read from storage has finished. */
  ready: boolean;
  settings: DeviceSettings;
  learners: LearnerProfile[];
  activeLearner?: LearnerProfile;
  /** Pass the profile itself right after creating it, before live queries catch up. */
  selectLearner: (learner: string | LearnerProfile | undefined) => Promise<void>;
  updateSettings: (patch: Partial<DeviceSettings>) => Promise<void>;
  updatePreferences: (patch: Partial<LearnerPreferences>) => Promise<void>;
}

export const AppStateContext = createContext<AppState | null>(null);

export function useAppState(): AppState {
  const context = useContext(AppStateContext);
  if (!context) throw new Error('useAppState must be used inside <AppStateProvider>');
  return context;
}

/** The active learner; only call inside routes guarded by the child layout. */
export function useLearner(): LearnerProfile {
  const { activeLearner } = useAppState();
  if (!activeLearner) throw new Error('No active learner');
  return activeLearner;
}
